import type {ReactElement} from 'react';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {AiCreditsPill} from '@Components/ai-credits-pill/ai-credits-pill';
import {CreditsPillSize, CreditsPillTheme} from '@Components/ai-credits-pill/ai-credits-pill.types';
import {AiChatTestId} from '@/test-ids/ai-chat-test-id';
import {Button, Size, Type} from '@Components/button';
import {openAiChatAuthenticateModal} from '@Libraries/ai-chat.library';
import {isUserPremium, isUserPremiumPlus} from '@Libraries/user.library';
import {AiChatSuperUserOptions} from '@Components/ai-chat-super-user-options/ai-chat-super-user-options';
import {AiChatCreditsDropdown} from '@AiChat/components/ai-chat-credits-dropdown/ai-chat-credits-dropdown';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {useAiChatSeason} from '@AiChat/hooks/useAiChatSeason';
import {getAiChatSeasonConfig} from '@AiChat/lib/ai-chat-season-library';
import styles from './ai-chat-account-status-bar.module.scss';

export function AiChatAccountStatusBar(): ReactElement {
  const {state, actions} = useAiChat();
  const season = useAiChatSeason();
  const {isSmall} = useAiChatSize();
  const isPremiumUser = isUserPremium() || isUserPremiumPlus();
  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MOBILE_MODAL_ACTIVATION_BREAKPONT);

  const handleLoginClick = (): void => {
    void openAiChatAuthenticateModal(actions.onLoginSuccessActions);
  };

  // CodeReviewMariamDone: why does the login button change for halloween? if needed, it should come from the season config, not here
  const getLoginButtonType = (): Type => {
    if (!season) {
      return Type.GHOST;
    }

    return getAiChatSeasonConfig(season).loginButtonType;
  };

  const getLoginButton = (): ReactElement | null => {
    if (state.isUserLoggedIn) {
      return null;
    }

    return (
      <Button text={window.i18next.t('pmwjs_user_login_action')} icon={'icon-sign-in'} type={getLoginButtonType()} customClasses="radius-8" size={Size.SMALL} onClick={handleLoginClick} />
    );
  };

  const getCreditsPill = (): ReactElement | null => {
    if (!state.isUserLoggedIn) {
      return null;
    }

    if (!isPremiumUser && !state.isPaygUserWithAiChatAccess) {
      return null;
    }

    if (!isPremiumUser && state.chatHistory.length === 0 && !state.currentChatId) {
      return null;
    }

    return (
      <AiChatCreditsDropdown
        selector={
          <div data-testid={AiChatTestId.CREDITS_PILL}>
            <AiCreditsPill credits={state.credits} theme={CreditsPillTheme.DEFAULT} size={CreditsPillSize.MEDIUM} />
          </div>
        }
      />
    );
  };

  const showPurchasePremiumDialog = (): void => {
    window.PMW.openPremiumPricingModal(false);
  };

  const getMobilePremiumButton = (): ReactElement => {
    return (
      <Button
        text={window.i18next.t('pmwjs_upgrade')}
        size={Size.XSMALL}
        type={Type.GHOST}
        textClasses={styles.mobileButtonTextColor}
        iconClassName={styles.mobileButtonTextColor}
        customClasses={'radius-8'}
        icon={'icon-crown'}
        onClick={showPurchasePremiumDialog}
      />
    );
  };

  const getPremiumButton = (): ReactElement | null => {
    if (!state.isUserLoggedIn) {
      return null;
    }

    if (isPremiumUser) {
      return null;
    }

    if (isSmall) {
      return getMobilePremiumButton();
    }

    return (
      <Button text={window.i18next.t('pmwjs_ai_credits_upgrade_for_more')} type={Type.PREMIUM} customClasses="radius-8" size={Size.SMALL} onClick={showPurchasePremiumDialog} />
    );
  };

  const getSuperUserOptions = (): ReactElement | null => {
    if (isMobile) {
      return null;
    }

    return <AiChatSuperUserOptions />;
  };

  if (isSmall) {
    return (
      <div className={'flex-row-align-center gap-2'}>
        {getLoginButton()}
        {getPremiumButton()}
        {getCreditsPill()}
        {getSuperUserOptions()}
      </div>
    );
  }

  return (
    <div className={'flex-row-align-center gap-2'}>
      {getLoginButton()}
      {getCreditsPill()}
      {getSuperUserOptions()}
      {getPremiumButton()}
    </div>
  );
}
